import React from 'react';
import { FormikValues } from 'formik';
import { intersectionWith } from 'lodash';

import {
  Content,
  ContentApproval,
  ContentCommentary,
  Group,
  GroupIdApproval,
  UserProfile,
  UserType,
} from 'api';
import { USER_FEED_ID } from 'constants/index';

import { Manual, Url } from './layouts';
import { Facebook, Instagram, Reddit, Twitter, YouTube } from './partials';
import { EmbedFacebook, EmbedInstagram, EmbedReddit, EmbedTwitter, EmbedYouTube } from './widgets';

type ContentType = Content | FormikValues;

const HOST_MATCHERS = {
  facebook: 'www.facebook.com',
  instagram: 'www.instagram.com',
  linkedin: 'www.linkedin.com',
  reddit: 'www.reddit.com',
  twitter: 'twitter.com',
  youtube: 'www.youtube.com',
  youtubeShortLink: 'youtu.be',
};

const isHostMatch = (content: ContentType, match: string) => {
  try {
    const { content_object } = content;

    if (!content_object.url) {
      return false;
    }

    const objectURL = new URL(content_object.url);
    return objectURL.hostname === match;
  } catch (e) {
    return false;
  }
};

export const getLayout = (content: ContentType) => {
  if (isFacebook(content)) {
    return Facebook;
  }

  if (isInstagram(content)) {
    return Instagram;
  }

  if (isReddit(content)) {
    return Reddit;
  }

  if (isTweet(content)) {
    return Twitter;
  }

  if (isYouTube(content)) {
    return YouTube;
  }

  if (isLinkedIn(content)) {
    return Url;
  }

  if (isUrl(content)) {
    return Url;
  }

  return Manual;
};

export const getNetworkName = (content: ContentType) => {
  if (isFacebook(content)) {
    return 'Facebook';
  }

  if (isInstagram(content)) {
    return 'Instagram';
  }

  if (isReddit(content)) {
    return 'Reddit';
  }

  if (isTwitter(content)) {
    return 'Twitter';
  }

  if (isYouTube(content)) {
    return 'YouTube';
  }

  if (isLinkedIn(content)) {
    return 'LinkedIn';
  }

  return null;
};

export const getEmbed = (content: ContentType) => {
  if (isFacebook(content)) {
    const url = getUrl(content);
    return <EmbedFacebook url={url} />;
  }

  if (isInstagram(content)) {
    const shortcode = getInstagramShortcode(content);
    return <EmbedInstagram shortcode={shortcode} />;
  }

  if (isReddit(content)) {
    const url = getUrl(content);
    return <EmbedReddit url={url} />;
  }

  if (isYouTube(content)) {
    const id = getYouTubeId(content);
    return <EmbedYouTube id={id} />;
  }

  if (isTwitter(content)) {
    const id = getTweetId(content);
    return <EmbedTwitter id={id} />;
  }

  return null;
};

export const isEmbed = (content: ContentType) => {
  return (
    isFacebook(content) ||
    isInstagram(content) ||
    isReddit(content) ||
    isTweet(content) ||
    isYouTube(content)
  );
};

export const isCreatingEmbed = (url: string) => {
  const objectURL = new URL(url);
  const isMatch = (matchUrl: string) => {
    return objectURL.hostname === matchUrl;
  };

  return (
    isMatch(HOST_MATCHERS.facebook) ||
    isMatch(HOST_MATCHERS.instagram) ||
    isMatch(HOST_MATCHERS.linkedin) ||
    isMatch(HOST_MATCHERS.reddit) ||
    isMatch(HOST_MATCHERS.twitter) ||
    isMatch(HOST_MATCHERS.youtube) ||
    isMatch(HOST_MATCHERS.youtubeShortLink)
  );
};

export const isLinkedIn = (content: ContentType) => {
  return isHostMatch(content, HOST_MATCHERS.linkedin);
};

export const isFacebook = (content: ContentType) => {
  return isHostMatch(content, HOST_MATCHERS.facebook);
};

export const isTwitter = (content: ContentType) => {
  const { content_object } = content;
  return !!content_object.tweet_metadata;
};

export const isInstagram = (content: ContentType) => {
  const { content_object } = content;
  return !!content_object.instagram_metadata;
};

export const isReddit = (content: ContentType) => {
  const { content_object } = content;
  return !!content_object.reddit_metadata;
};

export const isTweet = (content: ContentType) => {
  const { content_object } = content;
  return !!content_object.tweet_metadata;
};

export const isUrl = (content: ContentType) => {
  const { content_object } = content;
  return !!content_object.url_metadata;
};

export const isClientSideUrl = (content: ContentType) => {
  const { content_object } = content;
  return !!content_object.url;
};

export const isYouTube = (content: ContentType) => {
  const { content_object } = content;
  return !!content_object.youtube_metadata;
};

export const isPhoto = (content: ContentType) => {
  return !!getPhoto(content);
};

export const getInstagramShortcode = (content: ContentType) => {
  const { content_object } = content;
  const { instagram_metadata } = content_object;

  return instagram_metadata.shortcode;
};

export const getYouTubeId = (content: ContentType) => {
  const { content_object } = content;
  const { youtube_metadata } = content_object;

  return youtube_metadata.video_id;
};

export const getTweetId = (content: ContentType) => {
  const { content_object } = content;
  const { tweet_metadata } = content_object;

  return tweet_metadata.id_str;
};

export const getUrl = (content: ContentType) => {
  const { content_object } = content;
  return content_object.url;
};

export const getImage = (content: ContentType) => {
  if (!content) {
    return null;
  }

  const { content_object } = content;
  const { url_metadata, photos } = content_object;

  if (photos && photos.length) {
    return photos[0];
  }

  if (!url_metadata) {
    return null;
  }

  const { images } = url_metadata;
  return images ? images[0] : null;
};

export const getPhoto = (content: ContentType) => {
  const { content_object } = content;
  const { photos } = content_object;

  if (!photos) {
    return null;
  }

  return photos ? photos[0] : null;
};

export const getProviderDisplay = (content: ContentType) => {
  const { content_object } = content;
  const { url_metadata } = content_object;

  return url_metadata ? url_metadata.provider_display : '';
};

export const getProviderName = (content: ContentType) => {
  const { content_object } = content;
  const { url_metadata } = content_object;

  return url_metadata ? url_metadata.provider_name : '';
};

export const isPostCreator = (content: ContentType, user: UserProfile) => {
  if (!hasCreator(content)) {
    return false;
  }

  return content.user.id === user.id;
};

export const creatorIsAdmin = (content: ContentType) => {
  if (!content.user) {
    return false;
  }

  return content.user.user_type === UserType.Admin;
};

export const hasCreator = (content: ContentType) => {
  return !!content.user;
};

export const hasBody = (content: ContentType) => {
  const { content_object } = content;
  return !!content_object.body && content_object.body.length;
};

export const hasGroup = (content: ContentType) => {
  const { group_ids } = content;
  const groups = group_ids.filter((id: string) => id !== USER_FEED_ID);
  return !!groups.length;
};

export const hasUrl = (content: ContentType) => {
  const { content_object } = content;
  return !!content_object.url && content_object.url.length;
};

export const hasPhoto = (content: ContentType) => {
  return !!getPhoto(content);
};

export const isTextOnly = (content: ContentType) => {
  return !getPhoto(content) && !isUrl(content);
};

export const isShareable = (content: ContentType) => {
  return content.is_shareable;
};

export const isEngage = (content: ContentType) => {
  return content.is_engage;
};

export const canAllowSharing = (content: ContentType) => {
  return isClientSideUrl(content) || isEmbed(content) || isPhoto(content);
};

export const getGroupsByApproval = (content: ContentType, contentApproval: ContentApproval) => {
  const { groups, group_id_approvals } = content;

  return intersectionWith(
    groups,
    group_id_approvals,
    (group: Group, groupIdApproval: GroupIdApproval) => {
      if (group.id !== groupIdApproval.group_id) {
        return false;
      }

      const { approval } = groupIdApproval;
      return approval === contentApproval;
    },
  );
};

export const getApprovedGroups = (content: ContentType) => {
  const approvedGroups = getGroupsByApproval(content, ContentApproval.Approved);
  const autoApprovedGroups = getGroupsByApproval(content, ContentApproval.Autoapproved);

  return approvedGroups.concat(autoApprovedGroups);
};

export const getSubmittedGroups = (content: ContentType) => {
  return getGroupsByApproval(content, ContentApproval.Submitted);
};

export const hasCommentary = (content: ContentType) => {
  if (!content.commentary) {
    return false;
  }

  return content.commentary.some((commentary: ContentCommentary) => !!commentary.commentary);
};

export const isPinned = (content: ContentType) => {
  return content.is_pinned;
};
